import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import {
  PlatformInfoBannerSettingsResponseDto,
  UpdatePlatformInfoBannerSettingsRequestDto,
} from "../interfaces/platform-settings.interface";

export const platformSettingsKeys = {
  all: ["platform-settings"] as const,
  infoBanner: () => [...platformSettingsKeys.all, "info-banner"] as const,
};

export function usePlatformSettingsInfoBannerQuery(
  options?: Omit<UseQueryOptions<PlatformInfoBannerSettingsResponseDto>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...platformSettingsKeys.infoBanner(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<PlatformInfoBannerSettingsResponseDto>("/platform-settings/info-banner"),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

export function usePlatformSettingsUpdateInfoBanner(
  options?: Omit<
    UseMutationOptions<
      PlatformInfoBannerSettingsResponseDto,
      Error,
      UpdatePlatformInfoBannerSettingsRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: UpdatePlatformInfoBannerSettingsRequestDto) =>
      apiRequest<PlatformInfoBannerSettingsResponseDto>("/platform-settings/info-banner", "PATCH", data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: platformSettingsKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
  });
}
